import React, { useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { isValidMarkdown } from '../../utils/universalMarkdownParser';
import UniversalAccordion from './UniversalAccordion';

/**
 * Markdown File Import Component
 * Handles uploading and processing MD files for any task type
 */
const MarkdownFileImport = ({ 
  onImport, 
  taskType = null, 
  disabled = false,
  buttonText = "Import MD File",
  buttonVariant = "outlined",
  buttonSize = "small"
}) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [content, setContent] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleOpen = () => {
    setOpen(true);
    reset();
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const reset = () => {
    setFile(null);
    setContent('');
    setPreview(null);
    setError(null);
    setSuccess(false);
    setLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.toLowerCase().endsWith('.md')) {
      setError('Please select a Markdown (.md) file');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setError('File size must be less than 5MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
    readFileContent(selectedFile);
  };

  const readFileContent = (file) => {
    setLoading(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const fileContent = e.target.result;
      setContent(fileContent);
      
      // Validate markdown content
      if (!isValidMarkdown(fileContent)) {
        setError('The selected file does not appear to contain valid structured markdown content');
        setLoading(false);
        return;
      }

      // Generate preview
      setPreview(fileContent);
      setLoading(false);
      setSuccess(true);
    };
    
    reader.onerror = () => {
      setError('Failed to read file content');
      setLoading(false);
    };
    
    reader.readAsText(file);
  };

  const handleImport = () => {
    if (!content || !onImport) return;

    const importData = {
      content,
      file: file,
      fileName: file?.name,
      fileSize: file?.size,
      taskType: taskType,
      importedAt: new Date().toISOString()
    };

    onImport(importData);
    handleClose();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <>
      <Button
        startIcon={<AttachFileIcon />}
        onClick={handleOpen}
        disabled={disabled}
        variant={buttonVariant}
        size={buttonSize}
        sx={{ minWidth: 'auto' }}
      >
        {buttonText}
      </Button>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '500px', maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UploadIcon />
            <Typography variant="h6">Import Markdown File</Typography>
            {taskType && (
              <Chip 
                label={taskType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                size="small" 
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* File Selection */}
          {!file && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".md,.markdown"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id="markdown-file-input"
              />
              <label htmlFor="markdown-file-input">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<AttachFileIcon />}
                  sx={{ mb: 2 }}
                >
                  Select Markdown File
                </Button>
              </label>
              <Typography variant="body2" color="text.secondary">
                Upload a .md file downloaded from existing projects or any structured markdown content
              </Typography>
              
              <Box sx={{ mt: 3, textAlign: 'left' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Perfect for:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
                  {[
                    '📄 Downloaded project files',
                    '🔄 Reusing existing content',
                    '📋 Features from other projects',
                    '👥 User stories collections',
                    '🏗️ Architecture documents'
                  ].map((use) => (
                    <Chip 
                      key={use}
                      label={use} 
                      size="small" 
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
                
                <Typography variant="subtitle2" gutterBottom>
                  Supported formats:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {[
                    'Features (F001: Title)',
                    'User Stories (US-001: Title)',
                    'Architecture (## Component)',
                    'Tech Stack (## Technology)',
                    'Any structured markdown'
                  ].map((format) => (
                    <Chip 
                      key={format}
                      label={format} 
                      size="small" 
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          {/* Loading */}
          {loading && (
            <Box sx={{ py: 2 }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                Reading file content...
              </Typography>
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} action={
              <Button size="small" onClick={reset}>Try Again</Button>
            }>
              {error}
            </Alert>
          )}

          {/* Success - File Info */}
          {success && file && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon fontSize="small" />
                <Typography variant="body2">
                  File loaded successfully: <strong>{file.name}</strong> ({formatFileSize(file.size)})
                </Typography>
              </Box>
            </Alert>
          )}

          {/* File details */}
          {file && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">File Information</Typography>
                <Button size="small" onClick={reset}>Select Different File</Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`Name: ${file.name}`} size="small" />
                <Chip label={`Size: ${formatFileSize(file.size)}`} size="small" />
                <Chip label={`Lines: ~${content.split('\n').length}`} size="small" />
                {content && (
                  <Chip 
                    label={`Characters: ${content.length}`} 
                    size="small" 
                    color={content.length > 10000 ? 'warning' : 'default'}
                  />
                )}
              </Box>
            </Paper>
          )}

          {/* Preview */}
          {preview && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" />
                Content Preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ 
                maxHeight: '400px', 
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}>
                <UniversalAccordion 
                  content={preview} 
                  taskType={taskType}
                  title="Preview"
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!success || !content}
            startIcon={<UploadIcon />}
          >
            Import Content
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MarkdownFileImport;